import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  ISearcherConfiguration,
  PaxInfoModal,
  PaxStateChange,
  PaxStateError,
  PaxType,
  SearcherState
} from '@vueling-ng/vy-cdk-searcher';
import { VyModalService } from '@vueling-ng/vy-ds/src/lib/components/globals/modal';
import { AccountManagerResourcesService } from 'app/services/api-services/accountManager/accountManager.resources';
import { AnalyticsDataService } from 'app/services/external/analytics/analytics.service';
import { redirectRoutes_Legacy } from 'environments/common/redirect-routes';
import { environment } from 'environments/environment';
import { take } from 'rxjs';
import { PromotionalText, SearcherConfig } from './models/models';
import { modalLabelsDictionary, PaxInfoLabels, PaxInfoLabelsValues } from './services/enums/pax-info.values';
import { SearcherWrapperService } from './services/searcher-wrapper.service';

@Component({
  selector: 'app-searcher-wrapper',
  templateUrl: './searcher-wrapper.component.html',
  styleUrls: ['./searcher-wrapper.component.scss']
})
export class SearcherWrapperComponent {
  @Input({ required: true }) public culture = '';
  @Input({ required: true }) public currency = '';
  @Output() public blurEvent = new EventEmitter<boolean>();

  public searcherConfiguration: ISearcherConfiguration = SearcherConfig;

  private readonly _minimumGroupsPassengerAmount = 25;
  public promotionalTexts: string[] = PromotionalText;

  private readonly _translateService = inject(TranslateService);
  private readonly _modalService = inject(VyModalService);
  private readonly _searcherWrapperService = inject(SearcherWrapperService);
  private readonly _analyticsDataService = inject(AnalyticsDataService);
  private readonly _accountManagerResourcesService = inject(AccountManagerResourcesService);

  public controlClick(): void {
    this.blurEvent.emit(false);
  }

  public redirect(searcherState: SearcherState): void {
    this._analyticsDataService.trackEvent('search_start', searcherState);

    this._accountManagerResourcesService
      .createCookie()
      .pipe(take(1))
      .subscribe(() => {
        const passengerAmount = this._searcherWrapperService.calculateTotalPaxAfterSubmit(searcherState.pax);
        if (passengerAmount >= this._minimumGroupsPassengerAmount) {
          window.open(`${environment.groupsBaseUrl}${this._translateService.currentLang}/GroupSearch`);
        }
        window.open(
          `${redirectRoutes_Legacy.BookingFlow}${this.culture}${this._searcherWrapperService.generateSmartDeeplink(searcherState, this.culture, this.currency)}`
        );
      });
  }

  public openModalOnError(error: PaxStateError): void {
    if (PaxInfoLabelsValues.includes(error.errorType as PaxInfoLabels) && error.pax.paxType === PaxType.ExtraSeats) {
      this.openModal(modalLabelsDictionary[error.errorType as PaxInfoLabels]);
    }
  }

  public openModalOnUnacompaniedMinorsOrGroups(paxState: PaxStateChange): void {
    const totalPax = this._searcherWrapperService.countTotalPax(paxState.currentValues);

    if (totalPax >= this._minimumGroupsPassengerAmount) {
      this.openModal(modalLabelsDictionary['GroupsWarning']);
    }

    if (!this._searcherWrapperService.shouldOpenUnacompaniedMinorsModal(paxState.currentValues)) return;
    this.openModal(modalLabelsDictionary['Children']);
  }

  public openModalBasedOnPaxType(paxType: PaxType): void {
    this.openModal(modalLabelsDictionary[paxType as PaxInfoLabels]);
  }

  private openModal(modalInfo: PaxInfoModal): void {
    this._modalService.openModal(this._searcherWrapperService.generateModalConfig(modalInfo));
  }
}
